import { RegraCalculoPresenter } from '@/service/api/endpoints/regras-calculo'
import { normalize } from '@acme/common'
import { SN } from '@acme/core'

/** Config de adicional noturno resolvida a partir de uma regra representativa. */
export type NoturnoConfig = {
    label: string
    reducaoHrsATrabalhar: RegraCalculoPresenter['reducaoHrsATrabalhar']
    utilizaReducao: boolean
    horarioInicioAdicionalNoturno: RegraCalculoPresenter['horarioInicioAdicionalNoturno']
    horarioFimAdicionalNoturno: RegraCalculoPresenter['horarioFimAdicionalNoturno']
}

export type NoturnoPreview = {
    /** Configs distintas, deduplicadas por (início|fim|redução), em ordem de 1ª aparição. */
    configs: NoturnoConfig[]
    /** Índice da config default: grupo da "Regra Padrão", senão o mais frequente. */
    defaultIndex: number
}

const isRegraPadrao = (regra: RegraCalculoPresenter) =>
    normalize(regra.nome ?? '').toLowerCase() === 'regra padrao'

const configKey = (regra: RegraCalculoPresenter) =>
    [
        regra.horarioInicioAdicionalNoturno ?? '',
        regra.horarioFimAdicionalNoturno ?? '',
        SN.isTrue(regra.reducaoHrsATrabalhar) ? 'S' : 'N',
    ].join('|')

export function buildNoturnoPreview(
    regras: RegraCalculoPresenter[],
): NoturnoPreview {
    const grupos = new Map<string, RegraCalculoPresenter[]>()
    for (const regra of regras) {
        const key = configKey(regra)
        const grupo = grupos.get(key) ?? []
        grupo.push(regra)
        grupos.set(key, grupo)
    }

    const gruposArr = [...grupos.values()]

    const configs: NoturnoConfig[] = gruposArr.map(grupo => {
        const principal = grupo.find(isRegraPadrao) ?? grupo[0]
        const outros = grupo.length - 1
        const label =
            outros > 0 ? `${principal.nome} e mais ${outros}` : principal.nome
        const utilizaReducao = SN.isTrue(principal.reducaoHrsATrabalhar)
        return {
            label,
            reducaoHrsATrabalhar: principal.reducaoHrsATrabalhar,
            utilizaReducao,
            horarioInicioAdicionalNoturno: utilizaReducao
                ? principal.horarioInicioAdicionalNoturno
                : '',
            horarioFimAdicionalNoturno: utilizaReducao
                ? principal.horarioFimAdicionalNoturno
                : '',
        }
    })

    if (!configs.length) return { configs, defaultIndex: 0 }

    const padraoIndex = gruposArr.findIndex(g => g.some(isRegraPadrao))
    const maiorIndex = gruposArr.reduce(
        (maxI, g, i, arr) => (g.length > arr[maxI].length ? i : maxI),
        0,
    )

    return {
        configs,
        defaultIndex: padraoIndex >= 0 ? padraoIndex : maiorIndex,
    }
}
